import { apiRawGet, apiRawPost } from "./client";

async function safeRawGet(path: string): Promise<Response | null> {
  try {
    return await apiRawGet(path);
  } catch {
    return null;
  }
}

async function safeRawPost(
  path: string,
  body?: unknown,
): Promise<Response | null> {
  try {
    return await apiRawPost(path, body);
  } catch {
    return null;
  }
}

export function getModelDataByScenario(
  scenarioId: number,
): Promise<Response | null> {
  return safeRawGet(`/modelscenario/${scenarioId}/modeldata`);
}

export function getModelDataByScenarioWithSensitivity(
  scenarioId: number,
  sensitivityId: number,
): Promise<Response | null> {
  return safeRawGet(
    `/modelscenario/${scenarioId}/sensitivity/${sensitivityId}/modeldata`,
  );
}

export function compareScenariosFields(
  scenarioId1: number,
  scenarioId2: number,
  fieldIds: number[],
): Promise<Response | null> {
  return safeRawPost("/modelscenario/compare_scenarios_fields", {
    scenario_id_1: scenarioId1,
    scenario_id_2: scenarioId2,
    field_ids: fieldIds,
  });
}

export function compareScenarioSensitivityFields(
  scenarioId: number,
  sensitivityId: number,
  fieldIds: number[],
): Promise<Response | null> {
  return safeRawPost("/modelscenario/compare_scenario_sensitivity_fields", {
    scenario_id: scenarioId,
    sensitivity_id: sensitivityId,
    field_ids: fieldIds,
  });
}

async function pollCompareJob(
  jobId: string,
  maxAttempts: number = 150,
  intervalMs: number = 2000,
  onProgress?: (progress: number, total: number) => void,
): Promise<Response | null> {
  for (let i = 0; i < maxAttempts; i++) {
    const response = await safeRawGet(`/modelscenario/compare_job/${jobId}`);
    if (!response) return null;
    if (!response.ok) return response;

    const data = await response.json();
    if (data.status === "pending") {
      if (onProgress && data.progress != null && data.total != null) {
        onProgress(data.progress, data.total);
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      continue;
    }

    return new Response(JSON.stringify(data), {
      status: 200,
      headers: { "Content-Type": "application/json" },
    });
  }
  return new Response(
    JSON.stringify({ detail: "Compare job timed out" }),
    { status: 504, headers: { "Content-Type": "application/json" } },
  );
}

export async function compareScenariosFieldsFloatDetailed(
  scenarioId1: number,
  scenarioId2: number,
  fieldIds: number[],
  onProgress?: (progress: number, total: number) => void,
): Promise<Response | null> {
  const submitResponse = await safeRawPost(
    "/modelscenario/compare_scenarios_fields_float_detailed",
    {
      scenario_id_1: scenarioId1,
      scenario_id_2: scenarioId2,
      field_ids: fieldIds,
    },
  );
  if (!submitResponse || submitResponse.status !== 202) return submitResponse;
  const { job_id } = await submitResponse.json();
  return pollCompareJob(job_id, 150, 2000, onProgress);
}

export async function compareScenarioSensitivityFieldsFloatDetailed(
  scenarioId: number,
  sensitivityId: number,
  fieldIds: number[],
  sensitivityActivated: boolean,
  onProgress?: (progress: number, total: number) => void,
): Promise<Response | null> {
  const submitResponse = await safeRawPost(
    "/modelscenario/compare_scenario_sensitivity_fields_float_detailed",
    {
      scenario_id: scenarioId,
      sensitivity_id: sensitivityId,
      field_ids: fieldIds,
      sensitivity_activated: sensitivityActivated,
    },
  );
  if (!submitResponse || submitResponse.status !== 202) return submitResponse;
  const { job_id } = await submitResponse.json();
  return pollCompareJob(job_id, 150, 2000, onProgress);
}
